/**
 * Theme is session state, not server state — Zustand, not TanStack Query.
 * See ARCHITECTURE §6 for the state-ownership rules.
 *
 * Dark is the primary theme; light exists for printing and daylight offices.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHO OWNS WHICH AXIS, WHICH IS NOT THE SAME ANSWER FOR ALL THREE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *   Theme    light / dark        THE PERSON.       Per browser, per person, kept here.
 *   Palette  which neutral       THE ORGANISATION. Column on `organisations`, migration 0058.
 *   Accent   the brand hue       THE ORGANISATION. Same.
 *
 * The split is the difference between comfort and branding. Light-or-dark changes with the hour,
 * the room and the person, and nobody's employer has an opinion about it worth enforcing. The
 * palette and the accent are what the console LOOKS like — a tenant's people should all see the
 * tenant's look, rather than whichever one of them opened a picker last, and a platform operator
 * that has branded a customer's console should not have it undone one seat at a time.
 *
 * So the last two arrive from `GET /me`, already resolved (never null), and this store APPLIES
 * them. It does not choose them and there is no picker for them in the header any more: the
 * choice is made on the organisation, by an ancestor, in Access → the organisation drawer →
 * Appearance. Two controls for one fact is how they come to disagree.
 *
 * What survives here is `localStorage`, demoted from a preference to a HINT — see
 * `initialPalette()`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO AXES, AND `Theme` IS DELIBERATELY NOT ONE OF THEM
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * A palette is a second, ORTHOGONAL choice: `Theme` says light-or-dark, `Palette` says which
 * neutral the surfaces are mixed from. They multiply — four palettes x two modes — and
 * `tokens.css` resolves them by naming both attributes in one selector.
 *
 * THE OBVIOUS SHAPE WOULD HAVE BROKEN FIVE CALLERS SILENTLY. Widening `Theme` to
 * `'light' | 'dark' | 'slate' | …` is the first thing that comes to mind and it is wrong:
 *
 *   Chart.tsx, TimeSeriesChart.tsx, BucketedSeriesChart.tsx   ask `theme === 'dark'` to pick a
 *                                                             Highcharts palette
 *   FleetMap.tsx                                              keys `Record<Theme, string>` of
 *                                                             Mapbox style URLs off it
 *
 * Under a widened union the three chart comparisons quietly evaluate false — charts render
 * their LIGHT palette on a dark page, nothing throws, and it is only visible on screens that
 * happen to have a chart. The map would have failed louder (a type error demanding six style
 * URLs), which is the only reason that one would have been caught.
 *
 * So `Theme` keeps its meaning and its two values, every one of those callers is untouched,
 * and the palette travels beside it. The seam is that nothing outside this file and the
 * picker ever needs to know a palette exists.
 */
import { create } from 'zustand';
import { APPEARANCE_DEFAULTS, clampPct, ZOOM_MAX, ZOOM_MIN, type Surface } from './appearance';
/* Types only from the engine: `look.ts`, `look-data.ts` and `look-colour.ts` are loaded with `import()`
   for a look that is not the default (`applyLook`, below), never with the first paint. */
import type { AccentChoice, GlassSolution, LookSettings, ThemeChoice, WallpaperId } from './look';
import type * as LookPaint from './look-paint';
import { resetLookOnce } from './look-reset';
import { ACCENTS, PALETTES, type Accent, type Palette, type Theme } from './theme-vocabulary';

export { APPEARANCE_DEFAULTS, glowFactor, stepZoom, ZOOM_LEVELS, ZOOM_MAX, ZOOM_MIN, type Surface } from './appearance';

/*
 * The vocabulary lives in `theme-vocabulary.ts` (no side effects, so the pure look solver and its
 * tests can name it) and is re-exported here, where the reasons for it are written down.
 *
 * `Palette` — the neutral the chrome is mixed from. `thunder` is the original palette and has no
 * block in `tokens.css` at all — it IS the `:root` and `[data-theme]` blocks, so selecting it means
 * matching no palette selector and inheriting what was always there.
 *
 * THE VOCABULARY EXISTS FOUR TIMES AND THIS IS THE ONE THE OTHERS FOLLOW: the `org_palette` pg
 * enum (0058), the zod schema built from that enum's `enumValues`, the `[data-palette]` blocks in
 * `tokens.css`, and `theme-vocabulary.ts`. Adding one means all four in the same edit — the type
 * error in `lib/api/tenancy.ts` is the thing that will tell you, because it imports this union
 * rather than retyping it.
 */
export { ACCENTS, PALETTES, type Accent, type Palette, type Theme } from './theme-vocabulary';

/**
 * The brand hue — a THIRD axis, orthogonal to both of the others.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY IT IS NOT A PROPERTY OF THE PALETTE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The obvious shape is to let each palette carry its own accent — slate is orange, stone is
 * orange, thunder is lime. It is wrong for the same reason the mode is not a palette: the two
 * answer unrelated questions. "Which neutral are the surfaces mixed from" and "what colour is
 * this company" have nothing to do with each other, and coupling them would mean an operator
 * who wants warm greys is forced into a brand hue they did not choose, or vice versa.
 *
 * So they multiply: 4 palettes x 2 modes x 6 accents. `contrast.test.ts` measures every one of
 * the forty-eight, because `--brand`'s legibility depends on the ground it lands on and the
 * ground is the palette's.
 *
 * ── THE ORDER IS BY CLEARANCE, NOT BY TASTE ──────────────────────────────────
 * Ordered roughly by how much room each hue has before it collides with something that MEANS
 * something. `lime` (97° clear) and `violet` (65°) are unambiguous; `orange` (12° from danger)
 * and `teal` (14°, between success and path-b) are compromises that `tokens.css` names in full.
 * An operator picking from this list is picking how distinguishable their brand is from a fault,
 * and the order is the only place the UI can say so without a paragraph in a menu.
 *
 * ── THE ACCENT OWNS `--brand*` AND NOTHING ELSE ──────────────────────────────
 * Not `--success`, not `--warn`, not `--path-a`/`--path-b`, not `--sld-*`. Those carry meaning
 * — a verdict, a redundant path, a bus type — and an accent that re-hued them would be
 * changing what the console SAYS, not how it looks. `--ring` follows `--brand` and always has;
 * that is the one deliberate dependency, so a focus ring is visibly the product's colour.
 *
 * `Accent` and `ACCENTS` are in `theme-vocabulary.ts`. `lime` is the original and has no block in
 * `tokens.css` at all — selecting it means matching no accent selector and inheriting the brand the
 * mode blocks already define.
 */

/*
 * THE PREVIEW (Settings › Appearance's live preview, 2026-11-02). The settings page shows the real
 * console, shrunk, in an <iframe> of this same app with `?lm-embed=preview`. That copy must never
 * write the person's look back (it would overwrite their choice with whatever it was opened as), and
 * may show the other mode without changing theirs (`&lm-mode=light`). It follows the person's look
 * as it changes through the `storage` event, below.
 */
const PREVIEW: { on: boolean; mode: Theme | null } = (() => {
  try {
    const q = new URLSearchParams(window.location.search);
    const m = q.get('lm-mode');
    return { on: q.get('lm-embed') === 'preview', mode: m === 'light' || m === 'dark' ? m : null };
  } catch {
    return { on: false, mode: null };
  }
})();
export const isPreviewEmbed = (): boolean => PREVIEW.on;

/*
 * THE ONE-TIME RESET TO THE PLATFORM'S LOOK (Hamzah, 2026-11-03: "reset for everyone"). The default became
 * the Thunder glass look (Liquid, Thin, dark, Ember with the site photo) and the Sidebar layout, and every
 * browser that had saved a look or a layout before that is put back on it ONCE, so nobody keeps a look
 * from before it. The mark is the release's own: a later reset is a new mark, never a reuse of this one.
 * `index.html` runs the same few lines before the first paint, so the first frame is already the new look.
 * A person who picks a look afterwards keeps it: the mark is set, and this never runs again for them.
 */
export { LOOK_RESET_KEY, LOOK_RESET_MARK, resetLookOnce } from './look-reset';
resetLookOnce(typeof window === 'undefined' ? null : (() => { try { return window.localStorage; } catch { return null; } })(), PREVIEW.on);

const KEY = 'lumen_theme';
const PALETTE_KEY = 'lumen_palette';
const ACCENT_KEY = 'lumen_accent';

function initial(): Theme {
  if (PREVIEW.mode !== null) return PREVIEW.mode;
  try {
    const stored = localStorage.getItem(KEY);
    if (stored === 'light' || stored === 'dark') return stored;
    /* Nobody has chosen a mode or a look: the platform's default look is a dark one (Hamzah,
       2026-11-03), whatever the system prefers. Choosing a mode, or any look, ends this. */
    if (localStorage.getItem('lumen_look') === null && localStorage.getItem('lumen_surface') === null) return 'dark';
  } catch {
    /* private window, blocked storage — fall through */
  }
  return window.matchMedia?.('(prefers-color-scheme: light)').matches
    ? 'light'
    : 'dark';
}

/**
 * ─────────────────────────────────────────────────────────────────────────────
 * THE STORED PALETTE AND ACCENT ARE A HINT FOR ONE PAINT, NOT A PREFERENCE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The console renders before `/me` answers. Without a seed it paints in thunder/lime and then
 * repaints in the organisation's look a few hundred milliseconds later — a visible flash of a
 * brand that is not yours, on every single page load, which is exactly the thing branding exists
 * to avoid.
 *
 * So the last look `/me` reported is written to `localStorage` and read back here as the FIRST
 * paint's guess. The distinction from a preference matters and is the whole reason these two
 * functions still exist:
 *
 *   · nothing the PERSON does writes these keys any more — only `applyOrganisation`, from the
 *     server's answer
 *   · a stale hint is corrected the moment `/me` lands, and is never consulted again
 *   · so a wrong value costs one frame, never a wrong console
 *
 * Which is why the fallbacks below must agree with the SERVER's `DEFAULT_PALETTE`/`DEFAULT_ACCENT`
 * and are still not a second copy of them: they answer "what to draw before we have asked", and
 * the server answers "what this organisation actually looks like". If the platform re-brands, the
 * server moves and every console follows on its first `/me`; only the pre-answer frame lags.
 */
function initialAccent(): Accent {
  try {
    const stored = localStorage.getItem(ACCENT_KEY);
    if (ACCENTS.some((a) => a.id === stored)) return stored as Accent;
  } catch {
    /* private window, blocked storage — fall through */
  }
  return 'lime';
}

function initialPalette(): Palette {
  try {
    const stored = localStorage.getItem(PALETTE_KEY);
    if (PALETTES.some((p) => p.id === stored)) return stored as Palette;
  } catch {
    /* private window, blocked storage — fall through */
  }
  return 'thunder';
}

/**
 * Write both attributes, always together.
 *
 * `data-palette` is written even for `thunder`, which matches no selector in `tokens.css`. It
 * costs nothing and it means the DOM always states the full answer — someone inspecting a
 * misrendered page sees `thunder` rather than an absent attribute they have to know the
 * meaning of.
 */
function apply(theme: Theme, palette: Palette, accent: Accent): void {
  const root = document.documentElement;
  root.setAttribute('data-theme', theme);
  root.setAttribute('data-palette', palette);
  root.setAttribute('data-accent', accent);
  /* The person's own look (`useLook`) may show another palette or accent than the organisation's;
     `applyLook` writes what is actually shown over these two, after every change of either. */
  if (PREVIEW.on) return;
  try {
    /* `KEY` is the person's preference and is read back as one. The other two are the HINT — see
       `initialPalette()`. Written here rather than in `applyOrganisation` so the three keys can
       never disagree with the three attributes: one function writes both. */
    localStorage.setItem(KEY, theme);
    localStorage.setItem(PALETTE_KEY, palette);
    localStorage.setItem(ACCENT_KEY, accent);
  } catch {
    /* nothing to do — the attributes are already set */
  }
}

interface ThemeState {
  /** The PERSON's, and the only one they choose. */
  theme: Theme;
  /** The ORGANISATION's, applied from `/me`. */
  palette: Palette;
  /** The ORGANISATION's, applied from `/me`. */
  accent: Accent;
  toggle: () => void;
  set: (t: Theme) => void;
  /**
   * Paint what the server says this organisation looks like.
   *
   * Takes BOTH together because they arrive together and are written to the DOM together, and a
   * two-call version would repaint twice for one answer. Idempotent by design: `useMe` is read
   * from a dozen components and every one of them calls this, so it must be free to be called
   * with the value already in the store — the equality check below is what makes that true, and
   * without it Zustand would notify every subscriber on every render of every consumer.
   *
   * There is deliberately NO `setPalette`/`setAccent` any more. A person cannot choose these; an
   * ancestor organisation does, through `PATCH /organisations/:id`. A local setter would be a
   * second authority over one fact, and the local one always wins by being nearer — which is how
   * a tenant would quietly un-brand themselves and nobody would know why.
   */
  applyOrganisation: (palette: Palette, accent: Accent) => void;
}

export const useTheme = create<ThemeState>((set, get) => {
  const theme = initial();
  const palette = initialPalette();
  const accent = initialAccent();
  apply(theme, palette, accent);
  return {
    theme,
    palette,
    accent,
    toggle: () => {
      const next: Theme = get().theme === 'dark' ? 'light' : 'dark';
      apply(next, get().palette, get().accent);
      set({ theme: next });
    },
    set: (t) => {
      apply(t, get().palette, get().accent);
      set({ theme: t });
    },
    applyOrganisation: (p, a) => {
      const cur = get();
      if (cur.palette === p && cur.accent === a) return;
      apply(cur.theme, p, a);
      set({ palette: p, accent: a });
    },
  };
});

/**
 * ─────────────────────────────────────────────────────────────────────────────
 * THE PERSON'S LOOK: SOLID OR GLASS, ITS TWO DIALS, AND THE ZOOM (2026-10-26)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Hamzah, from the FESCO prototype: "have options to switch between glass look and the solid
 * also we can set the glass intensity like in the prototype and the zoom in out functionality
 * too". All four are the PERSON's, for the same reason light-or-dark is: they change with the
 * room, the screen and the eyes, and nobody's employer has an opinion about them worth
 * enforcing. So they live beside `Theme`, per browser, and never on the organisation.
 *
 *   surface    solid | glass   `data-surface` on <html>; the glass blocks in `tokens.css`
 *   glow       0–100 %         how strongly the accent glows behind the glass (dark only)
 *   strength   0–100 %         how see-through the glass cards are, and how much the floating
 *                              surfaces blur what is under them
 *   zoom       80–200 %        the whole console, applied by `lib/zoom.ts` while signed in
 *
 * SOLID IS THE DEFAULT and paints exactly what the console painted before this existed: no CSS
 * outside the glass blocks reads `--glow` or `--glass-strength`, and `--glass` resolves to
 * `none`. The clamps that keep every ratio over its floor are in `tokens.css`, so a slider here
 * can say 100 % without being able to break anything. The pure half — the vocabulary, the zoom
 * steps, the glow's curve — is `./appearance.ts`, testable without a document.
 */
const SURFACE_KEY = 'lumen_surface';
const ZOOM_KEY = 'lumen_zoom';

function storedNumber(key: string, fallback: number, lo: number, hi: number): number {
  try {
    const raw = localStorage.getItem(key);
    if (raw !== null && raw.trim() !== '' && Number.isFinite(Number(raw))) return clampPct(Number(raw), lo, hi);
  } catch {
    /* private window, blocked storage — fall through */
  }
  return fallback;
}

/** What the older Solid/Glass control kept, or null when it never kept anything (the person never chose). */
function storedSurface(): Surface | null {
  try {
    const raw = localStorage.getItem(SURFACE_KEY);
    if (raw === 'glass' || raw === 'solid') return raw;
  } catch {
    /* private window, blocked storage — fall through */
  }
  return null;
}

function persist(key: string, value: string): void {
  if (PREVIEW.on) return;
  try {
    localStorage.setItem(key, value);
  } catch {
    /* the attribute is already set; persistence is a convenience */
  }
}

/*
 * ─────────────────────────────────────────────────────────────────────────────
 * THE WHOLE LOOK (prototype v2, Settings › Appearance, 2026-11-02)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Everything Settings › Appearance offers — the theme and accent when the person uses their own,
 * Background colour, Accent fills, the material and its glass, the wallpaper or moving light, the
 * motion and the working mode — is ONE record, kept per browser like light-or-dark. `look.ts` says
 * what each field means and solves it; `look-paint.ts` paints it; this file holds the record and
 * paints the first frame.
 *
 * NO RECORD IS THE DEFAULT, AND IS EXACTLY TODAY'S CONSOLE: following the organisation's palette and
 * accent from `/me`, Solid, the console glow. The engine drops the record whenever the look is the
 * default again, so a cold load can tell "default" from one missing key and paint it with nothing
 * else loaded (the bundle budget). The organisation's own record, and who may set it (Access › the
 * organisation › Appearance), are unchanged.
 *
 * UNTIL THE ENGINE HAS READ IT, the record here is what was stored, unchecked — or, with none, just the
 * material a person chose under the old Solid/Glass control, which the engine then carries over in
 * full (`fullLook`). Settings fills it before it reads a field (`hydrateLook`).
 */
export const LOOK_KEY = 'lumen_look';
/** The last look this browser painted, applied as the first frame of the next load. */
export const PAINT_KEY = 'lumen_look_paint';
/** Your own wallpaper picture: a downscaled data URL in its own key, never inside the look. */
export const OWN_WALLPAPER_KEY = 'lumen_own_wallpaper';

export { persist as persistLocal };


function readStoredLook(): Partial<LookSettings> | null {
  try {
    const raw = localStorage.getItem(LOOK_KEY);
    const parsed: unknown = raw === null ? null : JSON.parse(raw);
    return parsed !== null && typeof parsed === 'object' ? (parsed as Partial<LookSettings>) : null;
  } catch {
    return null;
  }
}

interface LookState {
  look: LookSettings;
  /**
   * The person has a look of their own: a record in this browser, or a change made this session. False:
   * they have never chosen, so the platform's default look is derived afresh on every paint (the Thunder
   * look, or today's console for an organisation that branded itself), and follows it when it moves.
   */
  chosen: boolean;
  /** Change some of the look. Leaves a one-click working mode's mark unless `keepPreset`. */
  set: (patch: Partial<LookSettings>, keepPreset?: boolean) => void;
  /** Replace the whole look (Reset, Undo, the two one-press looks). */
  replace: (look: LookSettings) => void;
  /** Bumped when the own picture changes, so the layer repaints. */
  ownVersion: number;
  bumpOwn: () => void;
}

const storedLook = readStoredLook();
const oldSurface = storedLook === null ? storedSurface() : null;

export const useLook = create<LookState>((set, get) => ({
  /* With no record the store holds only what the first frame needs; the engine fills in the rest. */
  look: (storedLook ?? (oldSurface !== null ? { material: oldSurface } : {})) as LookSettings,
  chosen: storedLook !== null || oldSurface !== null,
  set: (patch, keepPreset) => {
    const next = { ...get().look, ...patch, ...(keepPreset ? {} : { preset: 'preset' in patch ? (patch.preset ?? null) : null }) } as LookSettings;
    persist(LOOK_KEY, JSON.stringify(next));
    set({ look: next, chosen: true });
  },
  replace: (look) => {
    persist(LOOK_KEY, JSON.stringify(look));
    set({ look: { ...look }, chosen: true });
  },
  ownVersion: 0,
  bumpOwn: () => set({ ownVersion: get().ownVersion + 1 }),
}));

/** What was painted last: the solve's answer and a key that changes with any colour. */
interface LookResult {
  /** Changes whenever a token a chart reads may have changed. Charts key their theme on it. */
  key: string;
  theme: ThemeChoice;
  accent: AccentChoice;
  wallpaper: WallpaperId;
  surface: Surface;
  solved: GlassSolution | null;
}

export const useLookResult = create<LookResult>(() => ({
  key: '',
  theme: 'classic-thunder',
  accent: 'theme',
  wallpaper: 'forest',
  surface: 'solid',
  solved: null,
}));

const surfaceOf = (look: Partial<LookSettings>): Surface => (look.material === 'glass' || look.material === 'granite' ? 'glass' : 'solid');

interface AppearanceState {
  /** Solid or Glass as the stylesheet sees it — Glass and Granite are both `glass`. A mirror of the
      look, for the callers that only need to know whether cards are see-through. */
  surface: Surface;
  /** The light behind the glass, 0–100 (the console glow's dial). Mirror of `look.light`. */
  glow: number;
  /** How see-through the glass cards are, 0–100. Mirror of `100 − look.frost`. */
  strength: number;
  /** The person's chosen level. `lib/zoom.ts` applies it only while the console is signed in. */
  zoom: number;
  setZoom: (pct: number) => void;
}

export const useAppearance = create<AppearanceState>((set) => {
  const look = useLook.getState().look;
  return {
    surface: surfaceOf(look),
    glow: look.light ?? APPEARANCE_DEFAULTS.glow,
    strength: 100 - (look.frost ?? 0),
    zoom: storedNumber(ZOOM_KEY, APPEARANCE_DEFAULTS.zoom, ZOOM_MIN, ZOOM_MAX),
    setZoom: (pct) => {
      const v = clampPct(pct, ZOOM_MIN, ZOOM_MAX);
      persist(ZOOM_KEY, String(v));
      set({ zoom: v });
    },
  };
});

export function mirrorAppearance(surface: Surface, look: Partial<LookSettings>): void {
  const glow = look.light ?? APPEARANCE_DEFAULTS.glow;
  const strength = 100 - (look.frost ?? 0);
  const a = useAppearance.getState();
  if (a.surface !== surface || a.glow !== glow || a.strength !== strength) useAppearance.setState({ surface, glow, strength });
}

interface NavState {
  collapsed: boolean;
  toggle: () => void;
  /**
   * Which nav groups are expanded, by label.
   *
   * ─────────────────────────────────────────────────────────────────────────────
   * WHY A SET OF OPEN GROUPS AND NOT A SINGLE ACTIVE ONE
   * ─────────────────────────────────────────────────────────────────────────────
   *
   * A true one-at-a-time drilldown closes what you were looking at every time you open
   * something else, and with five groups an operator comparing Integrations against
   * Telemetry would be re-opening a group on every switch. So several may be open, and the
   * DEFAULT is "only the group containing the current page" — which is the drilldown
   * behaviour where it matters (arriving somewhere) without the cost where it does not
   * (working across two areas).
   *
   * `null` means the store has not seeded yet, and `Shell` seeds it from the active tab on
   * first render. Distinguishing "not seeded" from "everything closed" matters: an operator
   * who deliberately collapses every group must not have that undone on the next render.
   */
  open: ReadonlySet<string> | null;
  toggleGroup: (label: string) => void;
  /** Seed on first render, from whichever group holds the active page. */
  seedGroups: (labels: readonly string[]) => void;
}

const NAV_KEY = 'lumen_nav_groups';

function storedGroups(): ReadonlySet<string> | null {
  try {
    const raw = localStorage.getItem(NAV_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every((x) => typeof x === 'string')) {
      return new Set(parsed);
    }
  } catch {
    /* private window, blocked storage, or a value from an older shape — reseed */
  }
  return null;
}

function persistGroups(open: ReadonlySet<string>): void {
  try {
    localStorage.setItem(NAV_KEY, JSON.stringify([...open]));
  } catch {
    /* the rail is already correct in memory; persistence is a convenience */
  }
}

/**
 * NARROW SCREENS START WITH THE RAIL COLLAPSED. Expanded, the rail is 238 px — on a 390 px phone
 * that left 152 px for the page (measured 2026-09-29): every card squeezed to a column of
 * truncated words. Below the `md` breakpoint it starts at icon width, and crossing the breakpoint
 * (rotating a tablet, resizing a window) moves it with the screen; the toggle still overrides.
 */
const NARROW_QUERY = '(max-width: 767px)';

function isNarrow(): boolean {
  try {
    return window.matchMedia(NARROW_QUERY).matches;
  } catch {
    return false;
  }
}

export const useNav = create<NavState>((set, get) => ({
  collapsed: isNarrow(),
  toggle: () => set({ collapsed: !get().collapsed }),

  open: storedGroups(),

  toggleGroup: (label) => {
    const current = get().open ?? new Set<string>();
    const next = new Set(current);
    if (next.has(label)) next.delete(label);
    else next.add(label);
    persistGroups(next);
    set({ open: next });
  },

  /*
   * Only ever seeds — never overrides. If storage already holds a choice, that choice is
   * the operator's and this is a no-op.
   */
  seedGroups: (labels) => {
    if (get().open !== null) return;
    const next = new Set(labels);
    persistGroups(next);
    set({ open: next });
  },
}));

try {
  window.matchMedia(NARROW_QUERY).addEventListener('change', (e) => useNav.setState({ collapsed: e.matches }));
} catch {
  /* no matchMedia (tests, very old browsers): the rail keeps whatever state it has */
}

/*
 * ─────────────────────────────────────────────────────────────────────────────
 * PAINTING THE LOOK
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Run whenever light-or-dark, the organisation's look or the person's look changes. A cold load runs
 * only the first two of three paths, and loads nothing of the engine for either (the bundle budget):
 *   1. No look of one's own: nothing painted — whatever an earlier look set is removed and the
 *      stylesheet's own values stand. Today's console, exactly.
 *   2. A look of one's own, before the engine has loaded: the LAST paint this browser computed, from
 *      its cache, so a v2 look never flashes the default (with no cache yet, the material at least).
 *   3. Then `look-paint.ts`, loaded with `import()`, recomputes it, paints it and caches it.
 */
let engine: typeof LookPaint | null = null;
let loading: Promise<void> | null = null;
let paintedVars: readonly string[] = [];
let paintedAttrs: readonly string[] = [];

export interface PaintCache {
  palette: Palette;
  accent: Accent;
  surface: Surface;
  attrs: Record<string, string>;
  vars: Record<string, string>;
  key: string;
}

/** Write a paint on <html>, removing whatever the last one set that this one does not. */
export function paintLook(palette: Palette, accent: Accent, surface: Surface, attrs: Readonly<Record<string, string>>, vars: Readonly<Record<string, string>>): void {
  const root = document.documentElement;
  root.setAttribute('data-palette', palette);
  root.setAttribute('data-accent', accent);
  root.setAttribute('data-surface', surface);
  for (const k of paintedAttrs) if (!(k in attrs)) root.removeAttribute(k);
  for (const [k, v] of Object.entries(attrs)) root.setAttribute(k, v);
  for (const k of paintedVars) if (!(k in vars)) root.style.removeProperty(k);
  for (const [k, v] of Object.entries(vars)) root.style.setProperty(k, v);
  paintedAttrs = Object.keys(attrs);
  paintedVars = Object.keys(vars);
}

/** Path 1: the default look, which paints nothing of its own. */
export function paintDefault(): void {
  const { theme: mode, palette, accent } = useTheme.getState();
  paintLook(palette, accent, 'solid', {}, {});
  useLookResult.setState({ key: `${mode}:${palette}:${accent}:solid`, theme: `classic-${palette}`, accent: `classic-${accent}`, surface: 'solid', solved: null });
  mirrorAppearance('solid', useLook.getState().look);
}

/**
 * THE FIRST FRAME OF THE PLATFORM'S DEFAULT, WITH NOTHING CACHED YET (Hamzah, 2026-11-03: the default is
 * now the glass Thunder look, and a first-ever load must not flash Solid before it). The engine is lazy
 * (the bundle budget), so until it arrives this is the stylesheet's own Glass — the blocks in
 * `tokens.css` and `glass.css` that paint without a solve — with the wallpaper chosen so the console's
 * own glow is not drawn. Every later load paints from the cache the engine writes (`PAINT_KEY`), which
 * is the real thing.
 */
const FIRST_FRAME_GLASS: Readonly<Record<string, string>> = { 'data-material': 'glass', 'data-glass-style': 'liquid', 'data-bg': 'wallpaper', 'data-frame': 'clear' };

function applyLook(): void {
  if (engine !== null) {
    engine.paintWithEngine();
    return;
  }
  let c: PaintCache | null = null;
  try {
    const raw = localStorage.getItem(PAINT_KEY);
    c = raw === null ? null : (JSON.parse(raw) as PaintCache);
  } catch {
    /* no cache: the material below */
  }
  const look = useLook.getState().look;
  if (c !== null) {
    paintLook(c.palette, c.accent, c.surface, c.attrs, c.vars);
    useLookResult.setState({ key: c.key, surface: c.surface });
    mirrorAppearance(c.surface, look);
  } else if (useLook.getState().chosen || look.material !== undefined) {
    const { palette, accent } = useTheme.getState();
    paintLook(palette, accent, surfaceOf(look), {}, {});
    mirrorAppearance(surfaceOf(look), look);
  } else {
    const { palette, accent } = useTheme.getState();
    paintLook(palette, accent, 'glass', FIRST_FRAME_GLASS, {});
    mirrorAppearance('glass', { frost: 30, light: 45 });
  }
  loading ??= import('./look-paint').then(
    (m) => {
      engine = m;
      applyLookSafely();
    },
    (err: unknown) => {
      loading = null;
      console.error('[look] engine', err);
    },
  );
}

/* A look that cannot be painted must never take the console down with it. */
function applyLookSafely(): void {
  try {
    applyLook();
  } catch (err) {
    console.error('[look] paint', err);
  }
}

applyLookSafely();
useTheme.subscribe(applyLookSafely);
useLook.subscribe(applyLookSafely);

/*
 * ANOTHER WINDOW OF THIS CONSOLE CHANGED THE LOOK — the settings page's live preview, or a second
 * tab. (The preview's light or dark comes from its own address, `lm-mode`.)
 */
try {
  window.addEventListener('storage', (e) => {
    if (e.key === LOOK_KEY) {
      if (e.newValue === null) useLook.setState({ chosen: false, look: {} as LookSettings });
      else {
        try {
          useLook.setState({ look: JSON.parse(e.newValue) as LookSettings, chosen: true });
        } catch {
          /* a damaged record from another window: keep ours */
        }
      }
    } else if (e.key === OWN_WALLPAPER_KEY) {
      useLook.getState().bumpOwn();
      applyLookSafely();
    }
  });
} catch {
  /* no window events (tests): nothing to follow */
}
